import { ReactElement, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { TbCircleDottedLetterN } from 'react-icons/tb';
import FormInput from '../../../../components/FormInput';
import AccordionIcon from './AccordionIcon';
import RunEntry from '../../../RunList/RunEntry/RunEntry';
import Button from '../../../../components/Button';
import {
  HiNoSymbol,
  HiOutlineNoSymbol,
  HiOutlinePlayCircle,
  HiOutlineTrash,
  HiOutlinePlusCircle,
  HiOutlineArrowPath,
  HiChevronUp,
  HiChevronDown,
  HiOutlinePencilSquare,
  HiOutlineCheckCircle,
  HiXMark,
} from 'react-icons/hi2';
import { dragFromAverages, dragFromDeltas } from '../../../../utils/drag';
import { dynamicPressureSource } from '../../../../utils/effectiveCalibration';
import { checkValueMatches } from '../../../RunList/RunEntry/driftMismatch';
import { numberToLetters } from '../../../../utils/letterNumber';
import {
  isDiscardConfig,
  isSpecialConfig,
  isSpecialLetterNumber,
} from '../../../../utils/configRole';
import GridContent from 'components/GridContent';
import { useFormContext } from 'react-hook-form';
import { useReprocess } from '../../../../hooks/useReprocess';
import { EditOperationMode, isOperationLocked } from './editOperationUtils';

const CALIBRATION_COPY_FIELDS = [
  'wheel_circumference', 'power_meter_slope', 'crr', 'cp_corr', 'yaw_offset', 'combined_mass',
  'cda_ref', 'xw_slope', 'xw_para_slope', 'xw_stall_angle', 'xw_stall_slope', 'xw_wheel_compensation',
  'chest_slope', 'chest_ref', 'head_slope', 'head_ref', 'airspeed_slope',
  'speed_sensor_pos', 'air_temp_offset', 'crr_temp_compensation_dict',
  'alt_power', 'alt_spd', 'gps_speed', 'combine_power', 'wheel_magnet_count', 'magnet_details',
  'autocal_params', 'autocal_skip', 'settings_collection',
  'gates_id',
];

// Display labels for the server-driven autocal run-param checkboxes; an
// unlisted param falls back to its raw name.
const AUTOCAL_PARAM_LABELS: Record<string, string> = {
  wheel_circumference: 'Wheel Circumference',
  yaw_offset: 'Yaw Offset',
  cp_corr: 'CP Corr',
  magnet_details: 'Magnet Spacing',
};

interface ConfigEntryProps {
  index: number;
  isEdit: boolean;
  originalConfig: any;
  showAllRuns: boolean;
  updateNextConfigId: any;
  plannedRunConfigIds: any[];
  setPlannedRunConfigIds: any;
  plannedRunNumbers: Record<number, number>;
  activeRunId: string | null;
  setActiveRunId: (id: string | null) => void;
  invalidateSession: any;
  reorderConfigLetters: () => void;
  configMovesToSave?: { [key: string]: () => Promise<void> };
  onSwapConfig?: (index: number, direction: 'up' | 'down') => void;
  changedConfigIds?: Set<string>;
  setChangedConfigIds?: (ids: Set<string>) => void;
  hasDuplicateDescription?: boolean;
  editOpMode?: EditOperationMode;
  setEditOpMode?: (mode: EditOperationMode) => void;
  onReprocessRun?: () => void;
  onEditConfig?: () => void;
  onSaveConfig?: () => void;
  onCancelConfig?: () => void;
  onInsertConfigBelow?: (index: number) => void;
  addingBelowConfigId?: string | null;
}

export default function ConfigEntry(props: ConfigEntryProps): ReactElement {
  const {
    index,
    isEdit,
    originalConfig,
    showAllRuns,
    updateNextConfigId,
    plannedRunConfigIds,
    setPlannedRunConfigIds,
    plannedRunNumbers,
    activeRunId,
    setActiveRunId,
    invalidateSession,
    reorderConfigLetters,
    configMovesToSave,
    onSwapConfig,
    changedConfigIds,
    setChangedConfigIds,
    hasDuplicateDescription,
    editOpMode,
    setEditOpMode,
    onReprocessRun,
    onEditConfig,
    onSaveConfig,
    onCancelConfig,
    onInsertConfigBelow,
    addingBelowConfigId,
  } = props;

  const { watch, setValue, getValues, formState } = useFormContext();
  const { isRunReprocessing } = useReprocess();
  const config = watch(`configs.${index}`);
  const configs = watch('configs');
  // Server-resolved calibration-drift tolerance bands (WARNING_CONSTS +
  // session_health_vars override), emitted at the session root by
  // SessionsTypeConfigRuns.calibration_drift_bands. Passed to RunEntry so the
  // mismatch asterisk uses the server source of truth, not hardcoded literals.
  const driftBands = watch('calibration_drift_bands');

  if (!config) {
    return <></>;
  }

  const [showRuns, setShowRuns] = useState(true);
  const [showAdvancedConfig, setShowAdvancedConfig] = useState(false);
  // Scroll-anchor: when this config's edit pencil is clicked, remember its
  // viewport position so the scroll can be compensated after the list grows.
  const cardRef = useRef<HTMLDivElement>(null);
  const editClickTopRef = useRef<number | null>(null);
  const [autocalBranch, setAutocalBranch] = useState<Record<string, boolean>>(() => {
    const params = config.autocal_params || {};
    const init: Record<string, boolean> = { manual_change: false };
    Object.entries(params).forEach(([key, value]) => {
      init[key] = !!value;
    });
    return init;
  });
  const configsById = (configs || []).reduce((map: any, config: any) => {
    if (config?.id) {
      map[config.id] = config;
    }
    return map;
  }, {});
  const parent = config.baseline_id && configsById[config.baseline_id];
  const parentDrag = parent && dragFromAverages(parent);
  const drag = dragFromAverages(config);
  const delta = dragFromDeltas(config);
  // The next run is the first planned entry that resolves to a live config —
  // `plannedRunNumbers` only carries an entry for those, so an orphaned id at
  // the head of the list can no longer swallow the "next" marker.
  const firstPlannedIndex = plannedRunConfigIds.findIndex(
    (_: any, i: number) => plannedRunNumbers[i] !== undefined,
  );
  const isNextRun = firstPlannedIndex >= 0 && plannedRunConfigIds[firstPlannedIndex] === config.id;
  const hasPlannedRun = plannedRunConfigIds.includes(config.id);

  const validConfigs = configs.filter(
    (c: any) => c && c?.letter_number !== undefined && c.letter_number < config.letter_number,
  );

  // Display letter should always be based on the current index position for valid configs
  // Find the index among non-deleted, ordinary (non-special-group) configs
  const displayIndex =
    configs
      .slice(0, index + 1)
      .filter((c: any) => !c.isDeleted && c.letter_number !== undefined && !isSpecialLetterNumber(c.letter_number))
      .length - 1;

  // Special groups (warmup, discard) show no letter — their description names them.
  const letter = isSpecialConfig(config) ? '' : numberToLetters(displayIndex);

  // Use useFieldArray directly in this component to avoid hook order issues
  const runs = watch(`configs.${index}.runs`);

  // Aggregate reprocess state across this config's runs.
  const configRuns: any[] = runs || [];
  // Hybrid session detection: does the whole session mix measured and
  // synthetic dynamic-pressure runs? Drives the M/S badge in RunEntry.
  const sessionDpSources = (configs || [])
    .flatMap((c: any) => c?.runs || [])
    .map(dynamicPressureSource)
    .filter(Boolean);
  const dpSourceMixed =
    sessionDpSources.includes('measured') && sessionDpSources.includes('synthetic');

  // Session-wide drift-mismatch census (each run judged against its own
  // config): when over half the session's runs carry a yellow/red asterisk,
  // the asterisk tooltips recommend reprocessing the whole session instead
  // of run-by-run.
  const sessionRunDrift = (configs || [])
    .flatMap((c: any) => (c?.runs || []).map((r: any) => ({ run: r, cfg: c })))
    .filter(({ run }: any) => run?.id);
  const driftMismatchCount = sessionRunDrift.filter(
    ({ run, cfg }: any) => checkValueMatches(run, cfg, driftBands).worstSeverity !== 'green',
  ).length;
  const recommendSessionReprocess = driftMismatchCount > sessionRunDrift.length / 2;
  const totalRunCount = configRuns.filter((r) => r?.id).length;
  const reprocessingRunCount = configRuns.filter((r) => r?.id && isRunReprocessing(r.id)).length;

  useEffect(() => {
    setShowRuns(showAllRuns);
  }, [showAllRuns]);

  // Entering edit mode makes every config taller; if this config's pencil was
  // the trigger, scroll to keep it visually fixed instead of letting it jump.
  useLayoutEffect(() => {
    if (isEdit && editClickTopRef.current != null && cardRef.current) {
      const newTop = cardRef.current.getBoundingClientRect().top;
      window.scrollBy(0, newTop - editClickTopRef.current);
    }
    editClickTopRef.current = null;
  }, [isEdit]);

  const originalLetter =
    originalConfig?.letter_number !== undefined ? numberToLetters(originalConfig.letter_number) : '';

  function addPlannedRun() {
    setPlannedRunConfigIds([...plannedRunConfigIds, config.id]);
  }

  function toggleAutocalSkip() {
    const newValue = !config.autocal_skip;
    setValue(`configs.${index}.autocal_skip`, newValue, { shouldDirty: true });
  }

  // Prefer the server-supplied schema (session.calibration_schema.autocal_run_params).
  // Fallback used when the response predates the schema field. Drives both the
  // checkbox row and the autocal_params rebuild on toggle.
  const autocalRunParams: string[] = getValues('calibration_schema')?.autocal_run_params
    ?? ['cp_corr', 'yaw_offset', 'wheel_circumference'];

  function updateAutocalBranch(field: string, value: boolean) {
    const newAutocalBranch = { ...autocalBranch, [field]: value };
    setAutocalBranch(newAutocalBranch);

    const autocalParameters = autocalRunParams;
    // Preserve result-param flags (xw_para_slope, airspeed_slope, chest_slope,
    // head_slope, cda_ref) from the server payload. Without this, the toggled
    // dict only carries the 3 run params and Pydantic AutoCalParams defaults
    // the missing keys to False on the server — silently flipping result flags.
    const baseParams = originalConfig?.autocal_params || {};
    const autocalObj: Record<string, boolean> = { ...baseParams };

    autocalParameters.forEach((param: string) => {
      autocalObj[param] = !!newAutocalBranch[param];
      if (!newAutocalBranch[param] && originalConfig?.autocal_params?.[param]) {
        setValue(`configs.${index}.${param}`, null, { shouldDirty: true });
      }
    });

    setValue(`configs.${index}.autocal_params`, autocalObj, { shouldDirty: true });
  }

  function handleToggleDestroy() {
    if (isOperationLocked(editOpMode, 'delete')) return;
    setEditOpMode?.('delete');
    const currentValue = !!getValues(`configs.${index}.isDeleted`);
    setValue(`configs.${index}.isDeleted`, !currentValue, { shouldDirty: true });
    reorderConfigLetters();
  }

  function handleSwapToBaseline() {
    if (isOperationLocked(editOpMode, 'reorder')) return;

    const confirmed = window.confirm(
      'This will promote this config to baseline. Manual calibration values on the current baseline may be overwritten. Continue?',
    );
    if (!confirmed) return;

    const currentConfigs = getValues('configs');
    if (!currentConfigs || currentConfigs.length < 2) return;

    const oldBaseline = currentConfigs[0];
    const targetIndex = currentConfigs.findIndex((c: any) => c.id === config.id);
    if (targetIndex <= 0) return;

    // Copy calibration fields from old baseline to this config
    for (const field of CALIBRATION_COPY_FIELDS) {
      if (oldBaseline[field] !== undefined) {
        setValue(`configs.${targetIndex}.${field}`, oldBaseline[field], { shouldDirty: true });
      }
    }

    // Build the new configs array: move target to position 0
    const newConfigs = [...currentConfigs];
    const [target] = newConfigs.splice(targetIndex, 1);

    // Update baseline references
    target.baseline_id = null;
    oldBaseline.baseline_id = target.id;

    // Also update any other configs that had the old baseline as parent to point to the new baseline
    for (const c of newConfigs) {
      if (c.id !== oldBaseline.id && c.baseline_id === oldBaseline.id) {
        c.baseline_id = target.id;
      }
    }

    newConfigs.unshift(target);
    setValue('configs', newConfigs, { shouldDirty: true });

    // Mark both configs as changed
    const newChangedIds = new Set(changedConfigIds);
    newChangedIds.add(target.id);
    newChangedIds.add(oldBaseline.id);
    setChangedConfigIds?.(newChangedIds);

    reorderConfigLetters();
    setEditOpMode?.('reorder');
  }

  const headerClasses = [
    'cr-col-span-7',
    'cr-relative',
    'cr-grid',
    'cr-grid-cols-subgrid',
    'cr-rounded-t-lg',
    'cr-border-2',
    'cr-border-solid',
    'cr-border-cirrusBlue-100',
    'cr-bg-cirrusBlue-50',
    showRuns ? 'cr-border-b-0' : 'cr-rounded-lg',
  ]
    .filter(Boolean)
    .join(' ');

  const descriptionClasses = [
    'cr-text-left',
    'cr-text-lg',
    'cr-font-bold',
    'cr-whitespace-nowrap',
    'cr-overflow-hidden',
    'cr-text-ellipsis',
    'cr-flex',
    'cr-items-center',
    'cr-gap-2',
    'cr-min-w-0',
    hasDuplicateDescription && !config.isDeleted ? 'cr-text-red-700' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={cardRef} className="cr-col-span-7 cr-mb-4 cr-grid cr-grid-cols-subgrid cr-shadow-lg">
      <div className={headerClasses}>
        {/* Deleted configs are veiled (not opacity-dimmed) so the action
            buttons can sit above the veil and stay clearly clickable. */}
        {config.isDeleted && (
          <div className="cr-pointer-events-none cr-absolute cr-inset-0 cr-z-20 cr-rounded-t-lg cr-bg-white/60" />
        )}
        {/* First Row: Chevron and Description */}
        <div className="cr-flex cr-items-center cr-justify-center cr-pt-2 cr-pb-1">
          <div
            onClick={() => {
              setShowRuns(!showRuns);
            }}
            className="cr-cursor-pointer"
          >
            <AccordionIcon show={showRuns} />
          </div>
        </div>
        <div className="cr-col-span-6 cr-flex cr-min-w-0 cr-items-center cr-justify-between cr-px-2 cr-pt-2 cr-pb-1">
          {isEdit && !isSpecialConfig(config) ? (
            <div className="cr-flex cr-w-full cr-min-w-0 cr-items-center cr-gap-2">
              <div className="cr-flex-1 cr-min-w-0">
                <FormInput
                  fieldName={`configs.${index}.description`}
                  rows={1}
                  required="Description is required"
                  customClass={`!cr-w-full cr-text-left cr-text-lg cr-font-bold ${
                    hasDuplicateDescription && !config.isDeleted ? '!cr-border-red-500 !cr-bg-red-50' : ''
                  }`}
                />
              </div>
              {hasDuplicateDescription && !config.isDeleted && (
                <div
                  className="cr-whitespace-nowrap cr-text-[17px] cr-font-medium cr-text-red-500 md:cr-text-sm"
                  title="Duplicate configuration name - this will cause server issues"
                >
                  ⚠ Duplicate
                </div>
              )}
            </div>
          ) : (
            <div className={descriptionClasses}>
              {config.description}
              {hasDuplicateDescription && !config.isDeleted && (
                <span
                  className="cr-text-[17px] cr-font-medium cr-text-red-500 md:cr-text-sm"
                  title="Duplicate configuration name - this will cause server issues"
                >
                  ⚠ Duplicate
                </span>
              )}
            </div>
          )}
          <div className="cr-relative cr-z-30 cr-ml-1 cr-flex cr-flex-shrink-0 cr-items-center cr-gap-2 md:cr-ml-2">
            {reprocessingRunCount > 0 && (
              <span
                className="cr-flex cr-items-center cr-gap-1 cr-text-xs cr-font-medium cr-text-blue-600"
                title={`${reprocessingRunCount} of ${totalRunCount} runs reprocessing`}
              >
                <HiOutlineArrowPath className="cr-size-4 cr-animate-spin" />
                {reprocessingRunCount}/{totalRunCount}
              </span>
            )}
            {isEdit ? (
              <label className="cr-flex cr-items-center cr-gap-2 cr-px-2 cr-py-1">
                <input
                  className="cr-size-5"
                  type="radio"
                  name="next"
                  value={config.id}
                  checked={isNextRun}
                  onChange={(e) => {
                    if (hasPlannedRun) {
                      updateNextConfigId(e.target.value);
                    } else {
                      setPlannedRunConfigIds((prev: string[]) => [config.id, ...prev]);
                    }
                  }}
                />
                <span className="cr-hidden cr-text-base cr-font-semibold md:cr-inline">Next</span>
              </label>
            ) : (
              hasPlannedRun && isNextRun && <TbCircleDottedLetterN size={28} className="cr-text-green-500" />
            )}
            {isEdit ? (
              <>
                <Button
                  onClick={() => onSaveConfig?.()}
                  icon={<HiOutlineCheckCircle className="cr-size-5" />}
                  variant="secondary"
                  className={editOpMode && editOpMode !== 'idle'
                    ? '!cr-bg-green-500 !cr-text-white !cr-border-green-500'
                    : ''}
                >
                  Save
                </Button>
                <Button
                  onClick={() => onCancelConfig?.()}
                  icon={<HiXMark className="cr-size-5" />}
                  variant="secondary"
                >
                  Cancel
                </Button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => {
                  editClickTopRef.current = cardRef.current?.getBoundingClientRect().top ?? null;
                  onEditConfig?.();
                }}
                className="cr-rounded cr-p-1 cr-text-blue-900 hover:cr-bg-cirrusBlue-100"
                title="Edit"
              >
                <HiOutlinePencilSquare className="cr-size-6" />
              </button>
            )}
          </div>
        </div>

        {/* Second Row: Config Letter and Drag */}
        <div className="cr-border-t-2 cr-border-cirrusBlue-100"></div>
        <div className="cr-flex cr-min-w-0 cr-items-center cr-justify-center cr-border-t-2 cr-border-cirrusBlue-100 cr-pl-0">
          <div className="cr-flex cr-items-center cr-gap-2">
            <span className="cr-text-lg cr-font-bold">{letter}</span>

            {changedConfigIds?.has(config.id) && originalLetter && originalLetter !== letter && (
              <span className="cr-text-[17px] cr-text-blue-900/60 md:cr-text-sm">({originalLetter})</span>
            )}
            {/* Up/Down arrows for reordering */}
            {isEdit && onSwapConfig && !isSpecialConfig(config) && (
              <div className="cr-flex cr-flex-col cr-gap-1">
                {/* Show up arrow if not baseline and not previous letter is baseline */}
                {index > 0 &&
                  (() => {
                    const previousConfig = configs[index - 1];
                    return previousConfig
                      && previousConfig.baseline_id !== config.id
                      && config.baseline_id !== previousConfig.id;
                  })() && (
                    <button
                      type="button"
                      onClick={() => onSwapConfig(index, 'up')}
                      className={`cr-p-0 cr-text-gray-600 hover:cr-text-gray-800 ${isOperationLocked(editOpMode, 'reorder') ? 'cr-opacity-40' : ''}`}
                      disabled={isOperationLocked(editOpMode, 'reorder')}
                      title={isOperationLocked(editOpMode, 'reorder') ? 'Save pending changes first' : 'Move up'}
                    >
                      <HiChevronUp className="cr-h-4 cr-w-4" />
                    </button>
                  )}
                {/* Show down arrow if not parent of next configuration */}
                {index < configs.length - 1 &&
                  (() => {
                    const nextConfig = configs[index + 1];
                    return (
                      nextConfig &&
                      nextConfig.baseline_id !== config.id &&
                      !isSpecialConfig(nextConfig)
                    );
                  })() && (
                    <button
                      type="button"
                      onClick={() => onSwapConfig(index, 'down')}
                      className={`cr-p-0 cr-text-gray-600 hover:cr-text-gray-800 ${isOperationLocked(editOpMode, 'reorder') ? 'cr-opacity-40' : ''}`}
                      disabled={isOperationLocked(editOpMode, 'reorder')}
                      title={isOperationLocked(editOpMode, 'reorder') ? 'Save pending changes first' : 'Move down'}
                    >
                      <HiChevronDown className="cr-h-4 cr-w-4" />
                    </button>
                  )}
              </div>
            )}
          </div>
        </div>
        <div className="cr-flex cr-min-w-0 cr-items-center cr-justify-center cr-border-t-2 cr-border-cirrusBlue-100 cr-pl-0">
          {isEdit && index > 0 && !isSpecialConfig(config) ? (
            <FormInput
              fieldName={`configs.${index}.baseline_id`}
              customClass="!cr-w-12 cr-text-lg cr-font-semibold cr-text-center"
              options={validConfigs}
              optionLabel="letter"
            />
          ) : parent && parent.letter_number !== undefined ? (
            <div className="cr-text-lg cr-font-semibold">{numberToLetters(parent.letter_number)}</div>
          ) : (
            <div></div>
          )}
        </div>
        <div className="cr-relative cr-flex cr-items-center cr-justify-center cr-border-t-2 cr-border-cirrusBlue-100 cr-py-1">
          <div className="cr-flex cr-items-center cr-gap-1">
            <div className="cr-text-center cr-text-lg cr-font-semibold">{drag && drag.toFixed(3)}</div>
          </div>
        </div>
        <div className="cr-flex cr-items-center cr-justify-center cr-border-t-2 cr-border-cirrusBlue-100 cr-py-1 ">
          {delta && (
            <span className={`cr-text-lg cr-font-semibold ${delta < 0 ? 'cr-text-blue-500' : 'cr-text-red'}`}>
              {delta > 0 ? '+' : ''}
              {delta.toFixed(3)}
            </span>
          )}
        </div>
        <div className="cr-flex cr-items-center cr-justify-center cr-border-t-2 cr-border-cirrusBlue-100 cr-py-1 ">
          <div className="cr-flex cr-gap-1">
            {config.autocal_skip && <HiOutlineNoSymbol className="cr-size-7" />}
            {isEdit &&
              configs.length > 1 &&
              config.letter_number !== 0 &&
              !isSpecialConfig(config) && (
                <>
                  <Button
                    className={`cr-ml-2 cr-rounded cr-bg-red-100 cr-py-2 cr-text-red-700 hover:cr-bg-red-200 md:cr-w-40 ${isOperationLocked(editOpMode, 'delete') ? 'cr-opacity-40' : ''}`}
                    onClick={handleToggleDestroy}
                    disabled={isOperationLocked(editOpMode, 'delete')}
                    icon={<HiOutlineTrash className="cr-size-5" />}
                  >
                    <span className="cr-hidden md:cr-inline">{config.isDeleted ? 'un-Delete' : 'Delete'}</span>
                  </Button>
                </>
              )}
          </div>
        </div>
      </div>

      {showRuns && (
        <GridContent spanCount={7}>
          {runs.map((run: any, runIndex: number) => {
            return (
              <RunEntry
                key={run.id}
                run={run}
                isNextRun={false}
                rowBgClass={runIndex % 2 === 1 ? 'cr-bg-cirrusBlue-10' : ''}
                config={config}
                driftBands={driftBands}
                baselineDrag={parentDrag}
                isEdit={isEdit}
                activeRunId={activeRunId}
                setActiveRunId={setActiveRunId}
                configs={configs}
                invalidateRuns={invalidateSession}
                hideColumns={{ rider: true, session: true, config: true }}
                context="sessions"
                configMovesToSave={configMovesToSave}
                dpSourceMixed={dpSourceMixed}
                recommendSessionReprocess={recommendSessionReprocess}
                editOpMode={editOpMode}
                setEditOpMode={setEditOpMode}
                onReprocessRun={onReprocessRun}
              />
            );
          })}
          {plannedRunConfigIds.map((configId: any, index: number) => {
            return (
              configId === config.id &&
              plannedRunNumbers[index] !== undefined && (
                <RunEntry
                  key={index}
                  config={config}
                  run={{ run_number: plannedRunNumbers[index], id: null }}
                  isNextRun={index === firstPlannedIndex}
                  isEdit={isEdit}
                  onDelete={() => {
                    const newPlannedRunConfigIds = [...plannedRunConfigIds];
                    newPlannedRunConfigIds.splice(index, 1);
                    setPlannedRunConfigIds(newPlannedRunConfigIds);
                  }}
                  activeRunId={activeRunId}
                  setActiveRunId={setActiveRunId}
                  configs={configs}
                  invalidateRuns={invalidateSession}
                  hideColumns={{ rider: true, session: true, config: true }}
                  context="sessions"
                />
              )
            );
          })}
          {isEdit && !isDiscardConfig(config) && (
            <>
              <div className="cr-col-span-5 cr-flex cr-items-center cr-justify-between cr-py-1">
                <div className="cr-flex cr-items-center cr-gap-2 cr-font-main">
                  <Button
                    className="cr-bg-cirrusBlue-50"
                    onClick={addPlannedRun}
                    icon={<HiOutlinePlusCircle className={'cr-size-5'} />}
                  >
                    Add run
                  </Button>
                  {/* Never on special rows: the warmup group is excluded from the
                      insert flow's orderedIds, so inserting "below" it would
                      resolve to position 0 and steal the baseline's letter A. */}
                  {!config.isDeleted && !isSpecialConfig(config) && (
                    addingBelowConfigId === config.id ? (
                      <span className="cr-flex cr-items-center cr-gap-2 cr-rounded-md cr-border cr-border-blue-900 cr-bg-cirrusBlue-50 cr-px-3 cr-py-2 cr-text-sm cr-font-semibold cr-text-blue-900">
                        <HiOutlineArrowPath className="cr-size-5 cr-animate-spin" />
                        Adding config...
                      </span>
                    ) : (
                      <span title={formState.isDirty ? 'Save your changes before adding a config' : undefined}>
                        <Button
                          className="cr-bg-cirrusBlue-50"
                          onClick={() => onInsertConfigBelow?.(index)}
                          icon={<HiOutlinePlusCircle className={'cr-size-5'} />}
                          disabled={isOperationLocked(editOpMode, 'create') || formState.isDirty}
                        >
                          Add Next Config
                        </Button>
                      </span>
                    )
                  )}
                </div>
                <div className="cr-py-1">
                  <div
                    className="cr-flex cr-cursor-pointer cr-items-center cr-gap-2 cr-font-main cr-text-[17px] cr-font-semibold md:cr-text-sm"
                    onClick={() => {
                      setShowAdvancedConfig(!showAdvancedConfig);
                    }}
                  >
                    <AccordionIcon show={showAdvancedConfig} />
                    <span>Advanced Configuration</span>
                  </div>
                </div>
              </div>

              {showAdvancedConfig && (
                <div className="cr-col-span-5 cr-py-1">
                  <div className="cr-mt-3 cr-space-y-3">
                    <div className="cr-flex cr-gap-2">
                      <Button
                        className="cr-bg-cirrusBlue-50"
                        onClick={toggleAutocalSkip}
                        icon={
                          config.autocal_skip ? (
                            <HiOutlinePlayCircle className={'cr-size-7'} />
                          ) : (
                            <HiNoSymbol className={'cr-size-7'} />
                          )
                        }
                      >
                        {config.autocal_skip ? 'Use' : 'Do Not Use'}
                      </Button>
                      {config.letter_number !== 0 && (
                        <Button
                          className={`cr-bg-amber-50 cr-text-amber-800 hover:cr-bg-amber-100 ${isOperationLocked(editOpMode, 'reorder') ? 'cr-opacity-40' : ''}`}
                          onClick={handleSwapToBaseline}
                          disabled={isOperationLocked(editOpMode, 'reorder')}
                          icon={<HiChevronUp className={'cr-size-5'} />}
                        >
                          Swap to Baseline
                        </Button>
                      )}
                    </div>

                    <div className="cr-flex cr-items-center cr-gap-4 cr-font-main cr-text-[17px] md:cr-text-sm">
                      <span>Update calibration autocal params:</span>
                      <div className="cr-flex cr-gap-4">
                        {autocalRunParams.map((param: string) => (
                          <label key={param} className="cr-flex cr-items-center cr-gap-1">
                            <input
                              type="checkbox"
                              className="cr-size-4"
                              checked={!!autocalBranch[param]}
                              onChange={(e) => updateAutocalBranch(param, e.target.checked)}
                            />
                            <span>{AUTOCAL_PARAM_LABELS[param] ?? param}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </GridContent>
      )}
    </div>
  );
}
